import { Component, OnInit } from "@angular/core";
import { Subject } from "rxjs";
import { FormGroup, FormBuilder } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { debounceTime } from "rxjs/operators";
import { ReportService } from "@services/report.service";
import { UsersService } from "@services/users.service";
import { DatePipe } from "@angular/common";

@Component({
    selector: "app-admin-report-modal",
    templateUrl: "./admin-report-modal.component.html",
    styleUrls: ["./admin-report-modal.component.scss"],
    standalone: false
})
export class AdminReportModalComponent implements OnInit {
  private _error = new Subject<string>();
  private _success = new Subject<string>();

  done = false;
  workflows: Object[];
  workflowsIds: number[] = [];
  pipe = new DatePipe("en-US"); // Use your own locale

  reportForm: FormGroup;
  errorMessage: string = "";
  successMessage = "";
  generatingConsolidated = false;
  savingSchedule = false;
  users: any[] = [];
  monthDays = Array.from({ length: 28 }, (_, index) => index + 1);
  scheduleTimeOptions = this.buildScheduleTimeOptions();
  availableScheduleTimeOptions = this.scheduleTimeOptions;
  activeReportTab = 'manual';
  private scheduleWorkflowIds: number[] = [];
  private readonly scheduleTimezone = 'America/Santiago';

  constructor(
    private _fboulder: FormBuilder,
    private modalService: NgbModal,
    private reportService: ReportService,
    private usersService: UsersService
  ) {}

  ngOnInit() {
    this.reportForm = this._fboulder.group({
      dateStart: ["", []],
      dateFinish: ["", []],
      scheduleEnabled: [false, []],
      scheduleFrequency: ["weekly", []],
      scheduleWeekday: ["friday", []],
      scheduleMonthDay: [1, []],
      scheduleTime: ["09:00", []],
      scheduleDateRange: ["all", []],
      scheduleRecipientUserIds: [[], []],
    });

    this.reportService.getWorkflows().subscribe((resp) => {
      this.workflows = resp["result"].map((item) => {
        return {
          ...item,
          isChecked: false,
          isScheduleChecked: this.scheduleWorkflowIds.includes(item.id)
        };
      });
    });

    this.usersService.getUsers().subscribe((resp) => {
      this.users = resp["result"].map(user => ({
        ...user,
        text: user.name ? `${user.name} - ${user.email}` : user.email
      }));
    });

    this.loadConsolidatedSchedule();

    this.reportForm.valueChanges.subscribe(() => this.refreshAvailableScheduleTimeOptions());

    this._success.subscribe((message) => (this.successMessage = message));
    this._success
      .pipe(debounceTime(8000))
      .subscribe(() => (this.successMessage = ""));

    this._error.subscribe((message) => (this.errorMessage = message));
    this._error
      .pipe(debounceTime(8000))
      .subscribe(() => (this.errorMessage = ""));
  }

  onSubmit() {
    const body = this.buildReportBody();

   this.reportService.generateReport(body).subscribe(
      response => {
        if (response['result']) {
          this._success.next('El reporte se está generando. Lo recibirás en tu correo en cuanto se encuentre listo.');
          this.done = true;
          setTimeout(() => this.closeModal(), 10000);
        } else {
          this._error.next('No se pudo generando el correo, Inténtelo de nuevo, por favor!!.');
        }
      },
    );
  }

  generateConsolidated() {
    const body = this.buildReportBody();
    this.generatingConsolidated = true;

    this.reportService.generateConsolidatedReport(body).subscribe(
      response => {
        this.generatingConsolidated = false;
        if (response['result']) {
          this._success.next('El reporte consolidado se está generando. Lo recibirás en tu correo en cuanto se encuentre listo.');
          setTimeout(() => this.closeModal(), 1800);
        } else {
          this._error.next('No se pudo generar el reporte consolidado.');
        }
      },
      () => {
        this.generatingConsolidated = false;
        this._error.next('No se pudo generar el reporte consolidado.');
      }
    );
  }

  loadConsolidatedSchedule() {
    this.reportService.getConsolidatedReportSchedule().subscribe(
      response => {
        const schedule = response['result'] || {};
        this.scheduleWorkflowIds = (schedule.workflows || []).map(id => Number(id));
        if (this.workflows) {
          this.workflows = this.workflows.map((workflow: any) => ({
            ...workflow,
            isScheduleChecked: this.scheduleWorkflowIds.includes(workflow.id)
          }));
        }
        const scheduleTime = this.formatScheduleTime(schedule.hour ?? 9, schedule.minute ?? 0);
        this.ensureScheduleTimeOption(scheduleTime);
        this.reportForm.patchValue({
          scheduleEnabled: !!schedule.enabled,
          scheduleFrequency: schedule.frequency || 'weekly',
          scheduleWeekday: schedule.weekday || 'friday',
          scheduleMonthDay: schedule.monthDay || 1,
          scheduleTime,
          scheduleDateRange: schedule.dateRange || 'all',
          scheduleRecipientUserIds: schedule.recipientUserIds || []
        });
        this.refreshAvailableScheduleTimeOptions();
      }
    );
  }

  saveConsolidatedSchedule() {
    const values = this.reportForm.value;
    if (!this.scheduleWorkflowIds.length) {
      this._error.next('Debes seleccionar al menos un flujo para programar el reporte consolidado.');
      return;
    }
    if (!values.scheduleRecipientUserIds || values.scheduleRecipientUserIds.length === 0) {
      this._error.next('Debes seleccionar al menos un usuario destinatario para la programación.');
      return;
    }
    const { hour, minute } = this.parseScheduleTime(values.scheduleTime);
    if (this.isScheduleTimeInPastForToday(values, hour, minute)) {
      this._error.next('No puedes programar una hora que ya pasó para el día de hoy. Selecciona una hora futura.');
      return;
    }
    if (this.isScheduleDayToday(values) && this.availableScheduleTimeOptions.length === 0) {
      this._error.next('Para hoy ya no hay horarios disponibles. Elige otro día o guarda la programación mañana.');
      return;
    }

    const body = {
      enabled: true,
      frequency: values.scheduleFrequency,
      weekday: values.scheduleWeekday,
      monthDay: values.scheduleMonthDay,
      hour,
      minute,
      dateRange: values.scheduleDateRange,
      recipientUserIds: values.scheduleRecipientUserIds || [],
      workflows: this.scheduleWorkflowIds
    };

    this.savingSchedule = true;
    this.reportService.saveConsolidatedReportSchedule(body).subscribe(
      response => {
        this.savingSchedule = false;
        if (response['result']) {
          this._success.next('Programación del reporte consolidado guardada correctamente.');
          setTimeout(() => this.closeModal(), 1800);
        } else {
          this._error.next('No se pudo guardar la programación del reporte consolidado.');
        }
      },
      error => {
        this.savingSchedule = false;
        this._error.next(error?.error?.message || 'No se pudo guardar la programación del reporte consolidado.');
      }
    );
  }

  buildReportBody() {
    const dateStart = this.reportForm.controls.dateStart.value;
    const dateFinish = this.reportForm.controls.dateFinish.value;
    const start = dateStart
      ? new Date(dateStart.year, dateStart.month - 1, dateStart.day)
      : "";

    const finish = dateFinish
      ? new Date(dateFinish.year, dateFinish.month - 1, dateFinish.day)
      : "";

    return {
      dateStart: this.pipe.transform(start || '', 'shortDate'),
      dateFinish:this.pipe.transform(finish || '', 'shortDate'),
      workflows: this.workflowsIds
    };
  }

  changeSelection(e, target: 'manual' | 'schedule' = 'manual') {
    const workflowId = Number(e.target.value);
    const flag = target === 'schedule' ? 'isScheduleChecked' : 'isChecked';
    this.workflows = this.workflows.map((workflow: any) =>
      workflow.id === workflowId ? { ...workflow, [flag]: e.target.checked } : workflow
    );
    const list = target === 'schedule' ? this.scheduleWorkflowIds : this.workflowsIds;
    if (e.target.checked) {
      if (!list.includes(workflowId)) {
        list.push(workflowId);
      }
    } else {
      const nextList = list.filter((item) => item !== workflowId);
      if (target === 'schedule') {
        this.scheduleWorkflowIds = nextList;
      } else {
        this.workflowsIds = nextList;
      }
    }
  }

  toggleAllWorkflows(e, target: 'manual' | 'schedule' = 'manual') {
    const checked = e.target.checked;
    const flag = target === 'schedule' ? 'isScheduleChecked' : 'isChecked';
    this.workflows = this.workflows.map((workflow: any) => ({ ...workflow, [flag]: checked }));
    const ids = checked ? this.workflows.map((workflow: any) => workflow.id) : [];
    if (target === 'schedule') {
      this.scheduleWorkflowIds = ids;
    } else {
      this.workflowsIds = ids;
    }
  }

  private buildScheduleTimeOptions() {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (const minute of [0, 15, 30, 45]) {
        options.push(this.formatScheduleTime(hour, minute));
      }
    }
    return options;
  }

  private ensureScheduleTimeOption(time: string) {
    if (!this.scheduleTimeOptions.includes(time)) {
      this.scheduleTimeOptions = [...this.scheduleTimeOptions, time].sort();
    }
  }

  private formatScheduleTime(hour: number, minute: number) {
    return `${this.padTimePart(hour)}:${this.padTimePart(minute)}`;
  }

  private parseScheduleTime(time: string) {
    const [hour, minute] = (time || '09:00').split(':').map(value => Number(value));
    return {
      hour: Number.isNaN(hour) ? 9 : hour,
      minute: Number.isNaN(minute) ? 0 : minute
    };
  }

  private padTimePart(value: number) {
    return value.toString().padStart(2, '0');
  }

  refreshAvailableScheduleTimeOptions() {
    const values = this.reportForm?.value || {};
    this.availableScheduleTimeOptions = this.getAvailableScheduleTimeOptions(values);
    const currentTime = values.scheduleTime;
    if (currentTime && !this.availableScheduleTimeOptions.includes(currentTime)) {
      this.reportForm.patchValue({
        scheduleTime: this.availableScheduleTimeOptions[0] || this.scheduleTimeOptions[0]
      }, { emitEvent: false });
    }
  }

  private getAvailableScheduleTimeOptions(values: any) {
    if (!this.isScheduleDayToday(values))
      return this.scheduleTimeOptions;

    const now = this.getNowInTimezone(this.scheduleTimezone);
    const currentMinutes = now.hour * 60 + now.minute;

    return this.scheduleTimeOptions.filter(time => {
      const { hour, minute } = this.parseScheduleTime(time);
      return hour * 60 + minute > currentMinutes;
    });
  }

  private isScheduleDayToday(values: any) {
    const now = this.getNowInTimezone(this.scheduleTimezone);

    if (values.scheduleFrequency === 'monthly') {
      const monthDay = values.scheduleMonthDay;
      if (monthDay === 'last')
        return now.day === this.getDaysInMonth(now.year, now.month, this.scheduleTimezone);
      return Number(monthDay) === now.day;
    }

    return this.getIsoWeekdayFromSchedule(values.scheduleWeekday) === now.isoWeekday;
  }

  private isScheduleTimeInPastForToday(values: any, hour: number, minute: number) {
    if (!this.isScheduleDayToday(values))
      return false;

    const now = this.getNowInTimezone(this.scheduleTimezone);
    const selectedMinutes = hour * 60 + minute;
    const currentMinutes = now.hour * 60 + now.minute;
    return selectedMinutes <= currentMinutes;
  }

  private getNowInTimezone(timezone: string) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(new Date());

    const get = (type: string) => parts.find(part => part.type === type)?.value || '0';
    const weekdayMap: Record<string, number> = {
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
      Sun: 7
    };

    return {
      year: Number(get('year')),
      month: Number(get('month')),
      day: Number(get('day')),
      hour: Number(get('hour')),
      minute: Number(get('minute')),
      isoWeekday: weekdayMap[get('weekday')] || 1
    };
  }

  private getIsoWeekdayFromSchedule(weekday: string) {
    const map: Record<string, number> = {
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
      sunday: 7
    };
    return map[(weekday || 'friday').toLowerCase()] || 5;
  }

  private getDaysInMonth(year: number, month: number, timezone: string) {
    const lastDayUtc = new Date(Date.UTC(year, month, 0, 12));
    const day = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      day: 'numeric'
    }).formatToParts(lastDayUtc).find(part => part.type === 'day')?.value;
    return Number(day || 0);
  }

  closeModal() {
    this.modalService.dismissAll();
  }
}
